// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { SerialPattern, SerialPatternKind } from '@/account/api/types';
import {
  isSerialPatternKind,
  serialPatternKindSchema,
} from '@/account/api/types';
import { useUpdateOrganization } from '@/account/hooks/use-update-organization';
import {
  MAX_SERIAL_PATTERNS,
  SERIAL_PATTERN_KIND_DISPLAY,
  SERIAL_PATTERN_KIND_ORDER,
  SERIAL_PATTERN_LENGTH_MAX,
  SERIAL_PATTERN_LENGTH_MIN,
  SERIAL_PATTERN_LENGTH_OPTIONS,
  SERIAL_PATTERN_PREFIX_MAX_LENGTH,
  SERIAL_PATTERN_PREFIX_PATTERN,
  serialPatternExamples,
  serialPatternKindExamples,
} from '@/account/utilities/serial-pattern-display';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import {
  Add as AddIcon,
  DeleteOutlineRounded,
  InfoOutlined,
} from '@/constants/hardfin-standard-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

/** A row mirrors the backend spec. */
const patternRowSchema = z
  .object({
    kind: serialPatternKindSchema,
    length: z.number().int().nullable(),
    prefix: z.string().nullable(),
  })
  .superRefine((row, context) => {
    const display = SERIAL_PATTERN_KIND_DISPLAY[row.kind];

    if (display.takesLength) {
      if (
        row.length === null ||
        row.length < SERIAL_PATTERN_LENGTH_MIN ||
        row.length > SERIAL_PATTERN_LENGTH_MAX
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['length'],
          message: `Enter a length between ${SERIAL_PATTERN_LENGTH_MIN} and ${SERIAL_PATTERN_LENGTH_MAX}`,
        });
      }
    }

    // The prefix is optional on every kind, so only its shape is checked.
    const prefix = row.prefix ?? '';
    if (
      prefix.length > SERIAL_PATTERN_PREFIX_MAX_LENGTH ||
      (prefix.length > 0 && !SERIAL_PATTERN_PREFIX_PATTERN.test(prefix))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prefix'],
        message: `Use up to ${SERIAL_PATTERN_PREFIX_MAX_LENGTH} letters, digits, dots, dashes, or underscores`,
      });
    }
  });

const formSchema = z
  .object({
    patterns: z.array(patternRowSchema).max(MAX_SERIAL_PATTERNS),
  })
  .superRefine((values, context) => {
    // Flag the later row of a repeated pair, not the first.
    const seen = new Map<string, number>();
    values.patterns.forEach((row, index) => {
      // Case-folded to match the backend, which folds case too.
      const signature = `${row.kind}|${row.length ?? ''}|${(
        row.prefix ?? ''
      ).toLowerCase()}`;
      if (seen.has(signature)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['patterns', index, 'kind'],
          message: 'This repeats an earlier pattern',
        });
        return;
      }
      seen.set(signature, index);
    });
  });

type FormData = z.infer<typeof formSchema>;

/** A new row starts on the most common shape with nothing filled in. */
const NEW_PATTERN_ROW: SerialPattern = {
  kind: 'alphanumeric',
  length: null,
  prefix: null,
};

interface Props {
  organizationKey: string;
  patterns: SerialPattern[];
  isOpen: boolean;
  onClose: () => void;
}

export function EditSerialPatternsDialog(props: Props) {
  const updateOrganizationMutation = useUpdateOrganization();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { control, formState, handleSubmit, reset, setValue } =
    useForm<FormData>({
      resolver: zodResolver(formSchema),
      defaultValues: { patterns: props.patterns },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'patterns',
  });

  const patterns = useWatch({ control, name: 'patterns' });

  // The watched copy lags the field array by one render after add or remove.
  const rows: SerialPattern[] =
    patterns?.length === fields.length ? patterns : fields;

  // Only an unprefixed fixed kind has one possible pattern to claim.
  const kindsAvailableToRow = (index: number) =>
    SERIAL_PATTERN_KIND_ORDER.filter((kind) => {
      if (SERIAL_PATTERN_KIND_DISPLAY[kind].takesLength) {
        return true;
      }

      return !rows.some(
        (other, otherIndex) =>
          otherIndex !== index &&
          other.kind === kind &&
          (other.prefix ?? '') === ''
      );
    });

  const isPending = updateOrganizationMutation.isPending;
  const isAtCap = fields.length >= MAX_SERIAL_PATTERNS;

  // Wired to the transitions, so a mid-edit refetch cannot reset the form.
  const resetToStoredPatterns = () => {
    reset({ patterns: props.patterns });
    setErrorMessage(null);
    updateOrganizationMutation.reset();
  };

  const handleKindChange = (index: number, kind: SerialPatternKind) => {
    const display = SERIAL_PATTERN_KIND_DISPLAY[kind];
    // Re-check by hand: setValue ignores reValidateMode.
    const options = {
      shouldDirty: true,
      shouldValidate: formState.isSubmitted,
    };
    setValue(`patterns.${index}.kind`, kind, options);
    if (!display.takesLength) {
      setValue(`patterns.${index}.length`, null, options);
    }
  };

  const onSubmit: SubmitHandler<FormData> = (values) => {
    setErrorMessage(null);
    updateOrganizationMutation.mutate(
      {
        orgKey: props.organizationKey,
        parameters: {
          serialPatterns: values.patterns.map((row) => ({
            ...row,
            // A cleared text field reads as '', which the backend refuses.
            prefix: row.prefix === '' ? null : row.prefix,
          })),
        },
      },
      {
        onSuccess: () => props.onClose(),
        onError: (error) =>
          setErrorMessage(getUserFacingErrorMessage(handleAPIError(error))),
      }
    );
  };

  const handleClose = () => {
    if (isPending) {
      return;
    }
    props.onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={props.isOpen}
      onClose={handleClose}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      TransitionProps={{
        onEnter: resetToStoredPatterns,
        onExited: resetToStoredPatterns,
      }}
    >
      <DialogTitle>Edit serial patterns</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" marginBottom={2}>
          A reported serial is accepted when it matches any pattern below. With
          no patterns, every serial is accepted.
        </Typography>

        {fields.length === 0 ? (
          <Box
            padding={3}
            border={1}
            borderColor="divider"
            borderRadius={1}
            textAlign="center"
          >
            <Typography variant="body2" color="text.secondary">
              No patterns are configured. Every reported serial is accepted.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            <Stack direction="row" spacing={2} paddingX={1.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                width="14rem"
              >
                Type
              </Typography>
              <Typography variant="caption" color="text.secondary" width="9rem">
                Prefix
              </Typography>
              <Typography variant="caption" color="text.secondary" width="7rem">
                Length
              </Typography>
              <Typography variant="caption" color="text.secondary" flex={1}>
                Example
              </Typography>
              <Box width="2.5rem" />
            </Stack>
            <Divider />
            {fields.map((field, index) => {
              const row = rows[index];
              const display = row
                ? SERIAL_PATTERN_KIND_DISPLAY[row.kind]
                : SERIAL_PATTERN_KIND_DISPLAY.alphanumeric;
              const examples = row ? serialPatternExamples(row) : null;

              return (
                <Stack
                  key={field.id}
                  direction="row"
                  spacing={2}
                  paddingX={1.5}
                  paddingY={1}
                  alignItems="flex-start"
                  border={1}
                  borderColor="divider"
                  borderRadius={1}
                >
                  <Box width="14rem">
                    <Controller
                      control={control}
                      name={`patterns.${index}.kind`}
                      render={({ field: kindField, fieldState }) => (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          disabled={isPending}
                          SelectProps={{
                            inputProps: {
                              'aria-label': `Pattern ${index + 1} type`,
                            },
                            // The icon belongs in the menu, not the box.
                            renderValue: (value) =>
                              isSerialPatternKind(value)
                                ? SERIAL_PATTERN_KIND_DISPLAY[value].label
                                : '',
                          }}
                          value={kindField.value}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          onChange={(event) =>
                            handleKindChange(
                              index,
                              event.target.value as SerialPatternKind
                            )
                          }
                        >
                          {kindsAvailableToRow(index).map((kind) => (
                            <MenuItem key={kind} value={kind}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                width="100%"
                              >
                                <Box flex={1}>
                                  {SERIAL_PATTERN_KIND_DISPLAY[kind].label}
                                </Box>
                                <KindExamplesTooltip kind={kind} />
                              </Stack>
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Box>

                  <Box width="9rem">
                    <Controller
                      control={control}
                      name={`patterns.${index}.prefix`}
                      render={({ field: prefixField, fieldState }) => (
                        <TextField
                          fullWidth
                          size="small"
                          disabled={isPending}
                          inputProps={{
                            'aria-label': `Pattern ${index + 1} prefix`,
                          }}
                          value={prefixField.value ?? ''}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          onChange={(event) =>
                            prefixField.onChange(event.target.value)
                          }
                        />
                      )}
                    />
                  </Box>

                  <Box width="7rem">
                    {display.takesLength ? (
                      <Controller
                        control={control}
                        name={`patterns.${index}.length`}
                        render={({ field: lengthField, fieldState }) => (
                          <TextField
                            select
                            fullWidth
                            size="small"
                            disabled={isPending}
                            SelectProps={{
                              inputProps: {
                                'aria-label': `Pattern ${index + 1} length`,
                              },
                            }}
                            value={lengthField.value ?? ''}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                            onChange={(event) =>
                              lengthField.onChange(Number(event.target.value))
                            }
                          >
                            {SERIAL_PATTERN_LENGTH_OPTIONS.map((length) => (
                              <MenuItem key={length} value={length}>
                                {length}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    ) : (
                      <InapplicableCell />
                    )}
                  </Box>

                  <Box flex={1} paddingTop={1}>
                    <Typography
                      variant="body2"
                      color={
                        examples?.isExact ? 'text.primary' : 'text.disabled'
                      }
                      sx={{ overflowWrap: 'anywhere' }}
                    >
                      {examples?.values[0] ?? '—'}
                    </Typography>
                  </Box>

                  <Box paddingTop={0.5}>
                    <IconButton
                      size="small"
                      aria-label={`Remove pattern ${index + 1}`}
                      disabled={isPending}
                      onClick={() => remove(index)}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          marginTop={2}
        >
          <ConditionalTooltip
            arrow
            tooltipText={
              isAtCap
                ? `An organization can have at most ${MAX_SERIAL_PATTERNS} patterns`
                : undefined
            }
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              disabled={isAtCap || isPending}
              onClick={() => append(NEW_PATTERN_ROW)}
            >
              Add pattern
            </Button>
          </ConditionalTooltip>
          <Typography variant="caption" color="text.secondary">
            {fields.length} of {MAX_SERIAL_PATTERNS} patterns
          </Typography>
        </Stack>

        {formState.errors.patterns?.message && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formState.errors.patterns.message}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          variant="contained"
          disabled={isPending || !formState.isDirty}
        >
          Save patterns
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface KindExamplesTooltipProps {
  kind: SerialPatternKind;
}

/** The info icon beside a kind, listing every form it accepts. */
function KindExamplesTooltip(props: KindExamplesTooltipProps) {
  const display = SERIAL_PATTERN_KIND_DISPLAY[props.kind];
  const examples = serialPatternKindExamples(props.kind);

  return (
    <Tooltip
      arrow
      placement="right"
      title={
        <Box>
          <Typography variant="caption" component="div">
            {display.description}
          </Typography>
          <Typography variant="caption" component="div" marginTop={0.5}>
            Examples:
          </Typography>
          {examples.map((example) => (
            <Typography key={example} variant="caption" component="div">
              {example}
            </Typography>
          ))}
        </Box>
      }
    >
      <InfoOutlined
        fontSize="small"
        color="action"
        aria-label={`${display.label} examples`}
      />
    </Tooltip>
  );
}

/** The cell a kind does not use, held so the grid stays aligned. */
function InapplicableCell() {
  return (
    <Typography
      variant="body2"
      color="text.disabled"
      textAlign="center"
      paddingTop={1}
    >
      —
    </Typography>
  );
}
